'use strict';

/**
 * Global CSRF auto-retry.
 *
 * The server CSRF middleware (scripts/middleware/csrf.js) answers an expired/invalid
 * token on an AJAX request with `403 { csrfError, csrfRetry, csrf: { tokenName, token } }`
 * instead of redirecting to the CSRF-Fail page.
 *
 * This registers a single jQuery ajaxPrefilter that, on such a response, writes the
 * fresh token into the DOM and the in-flight request payload and transparently
 * re-submits the original request exactly once — so the shopper keeps their filled-in
 * form and never sees the CSRF-Fail page.
 *
 * jQuery runs prefilters before it binds the caller's success/error callbacks, so
 * replacing `options.error` here cleanly intercepts the failure without editing any of
 * the ~25 call sites. The `_csrfRetried` guard guarantees exactly one retry.
 */

const $ = require('jquery');

/**
 * Escape RegExp metacharacters so a string can be embedded literally in a pattern.
 * @param {string} str - raw string
 * @returns {string} escaped string
 */
function escapeRegExp(str) {
    return String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * True when the request URL targets the current origin (relative, or absolute/
 * protocol-relative with a matching host). Cross-origin requests are left untouched so
 * a third-party response can never poison our token inputs or drive a retry.
 * @param {string} url - request URL
 * @returns {boolean} whether the URL is same-origin
 */
function isSameOrigin(url) {
    if (!url) {
        return true;
    }
    // Neither absolute nor protocol-relative → relative to the current page.
    if (!/^([a-z][a-z0-9+.-]*:)?\/\//i.test(url)) {
        return true;
    }
    const link = document.createElement('a');
    link.href = url;
    return (link.protocol + '//' + link.host) === (window.location.protocol + '//' + window.location.host);
}

/**
 * Replace (or append) the csrf token value inside an already-built request payload.
 * @param {string|Object} data - the original request data (serialized string or object)
 * @param {string} name - the token field name (always "csrf_token")
 * @param {string} value - the fresh token value
 * @returns {string|Object} the payload carrying the fresh token
 */
function rewriteToken(data, name, value) {
    if (data === null || typeof data === 'undefined') {
        return data;
    }

    if (typeof data === 'string') {
        const tokenPattern = new RegExp('([?&]?' + escapeRegExp(name) + '=)[^&]*');
        if (tokenPattern.test(data)) {
            return data.replace(tokenPattern, '$1' + encodeURIComponent(value));
        }
        return data + (data.length ? '&' : '') + name + '=' + encodeURIComponent(value);
    }

    if (typeof data === 'object') {
        data[name] = value;
        return data;
    }

    return data;
}

module.exports = function registerCsrfRetry() {
    $.ajaxPrefilter(function (options, originalOptions) {
        // Never intercept multipart/file uploads (payload can't be safely rewritten
        // here) or cross-origin requests (a third-party response must not be able to
        // poison our token inputs or trigger a retry).
        if (options.processData === false
            || (typeof FormData !== 'undefined' && originalOptions.data instanceof FormData)
            || !isSameOrigin(options.url)) {
            return;
        }

        const callerError = originalOptions.error;

        options.error = function (jqXHR) {
            const resp = jqXHR.responseJSON;

            if (resp && resp.csrfRetry && resp.csrf && !originalOptions._csrfRetried) {
                const name = resp.csrf.tokenName;
                const value = resp.csrf.token;

                // 1) Refresh every token input already in the DOM. Match by the real
                //    name property (no selector interpolation) so a crafted tokenName
                //    can never be turned into a selector.
                $('input[name]').filter(function () {
                    return this.name === name;
                }).val(value);

                // 2) Rebuild the original request with the fresh token, a guard flag,
                //    and an X-CSRF-Retry header so the server can tell this benign
                //    stale-token retry from a genuine failure (a retry that still
                //    fails). Reuses the caller's own success/error callbacks.
                const retryOptions = $.extend({}, originalOptions, { _csrfRetried: true });
                retryOptions.data = rewriteToken(originalOptions.data, name, value);
                retryOptions.headers = $.extend({}, originalOptions.headers, { 'X-CSRF-Retry': '1' });

                $.ajax(retryOptions);
                return; // swallow: the caller's error (and its redirect) never runs
            }

            // Not a retriable CSRF failure (or already retried once) — hand control
            // back to the caller's original error handler.
            if (typeof callerError === 'function') {
                return callerError.apply(this, arguments);
            }

            return undefined;
        };
    });
};
